import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  SectionTitle, 
  Paragraph, 
  Callout,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox 
} from '@/components/WorkbookPage';
import { Map, Target, Calendar, Star, Rocket } from 'lucide-react';

export const LifeMoneyMapPage = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.capstone || {};
  
  const updateData = (field, value) => {
    updateFormData('capstone', { ...data, [field]: value });
  };
  
  const seasons = [
    { id: 'survival', name: 'Survival Mode', description: 'Crisis, paycheck to paycheck, basic needs barely covered' },
    { id: 'stability', name: 'Stability', description: 'Bills paid, small buffer, working toward control' },
    { id: 'growth', name: 'Growth', description: 'Debt decreasing, savings increasing, building for future' },
  ];
  
  return (
    <WorkbookPage pageId="life-money-map" prevPage="section-5-divider" nextPage="commitment">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <Map className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Your Life & Money Map
          </h1>
        </div>
        <p className="text-lg text-muted-foreground">
          Bringing it all together into a personalized action plan
        </p>
      </header>
      
      <Paragraph>
        You've learned the rules. Now it's time to create YOUR map—a personalized plan that 
        meets you where you are and guides you where you want to go. This isn't about being 
        perfect. It's about being intentional.
      </Paragraph>
      
      {/* Current Season */}
      <SectionTitle>Step 1: Identify Your Current Season</SectionTitle>
      <Paragraph>
        Where are you right now? Be honest—there's no shame in any season. You can only 
        move forward from where you actually are.
      </Paragraph>
      
      <div className="grid gap-4 my-6">
        {seasons.map((season) => (
          <button
            key={season.id}
            onClick={() => updateData('currentSeason', season.id)}
            className={`text-left p-5 rounded-xl border-2 transition-all ${
              data.currentSeason === season.id
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <h4 className="font-semibold mb-1">{season.name}</h4>
            <p className="text-sm text-muted-foreground">{season.description}</p>
          </button>
        ))}
      </div>
      
      {/* Top Values Recap */}
      <SectionTitle>Step 2: Confirm Your Top Values</SectionTitle>
      <Paragraph>
        From Rule 13, what are the values driving your financial decisions?
      </Paragraph>
      
      <div className="bg-muted/30 rounded-xl p-5 my-6">
        <WorksheetInput
          label="My top 3-5 values:"
          placeholder="Family, Security, Freedom, etc."
          value={data.valuesRecap || ''}
          onChange={(v) => updateData('valuesRecap', v)}
        />
      </div>
      
      {/* 90-Day Goals */}
      <SectionTitle>
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Step 3: Your 90-Day Goals
        </div>
      </SectionTitle>
      <Paragraph>
        What can you realistically accomplish in the next 3 months? Be specific and measurable.
      </Paragraph>
      
      <div className="bg-muted/30 rounded-xl p-6 my-6 border border-border">
        <div className="space-y-4">
          {[1, 2, 3].map((num) => (
            <div key={num} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">
                {num}
              </div>
              <input
                type="text"
                className="flex-1 border-b border-dashed border-border bg-transparent py-2 focus:outline-none focus:border-primary"
                placeholder={`90-day goal ${num}`}
                value={data[`goal90_${num}`] || ''}
                onChange={(e) => updateData(`goal90_${num}`, e.target.value)}
              />
            </div>
          ))}
        </div>
        
        <Callout type="tip" title="Examples of 90-Day Goals">
          <ul className="text-sm space-y-1">
            <li>• "Save $500 for starter emergency fund"</li>
            <li>• "Pay off smallest debt ($300 medical bill)"</li>
            <li>• "Set up 3-account system by [date]"</li>
            <li>• "Track every expense for 30 days"</li>
          </ul>
        </Callout>
      </div>
      
      {/* 12-Month Goals */}
      <SectionTitle>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Step 4: Your 12-Month Goals
        </div>
      </SectionTitle>
      <Paragraph>
        Looking ahead one year, what do you want to have accomplished?
      </Paragraph>
      
      <div className="bg-muted/30 rounded-xl p-6 my-6 border border-border">
        <div className="space-y-4">
          {[1, 2, 3].map((num) => (
            <div key={num} className="flex items-start gap-3">
              <Star className="h-5 w-5 text-accent mt-2 shrink-0" />
              <input
                type="text"
                className="flex-1 border-b border-dashed border-border bg-transparent py-2 focus:outline-none focus:border-primary"
                placeholder={`12-month goal ${num}`}
                value={data[`goal12_${num}`] || ''}
                onChange={(e) => updateData(`goal12_${num}`, e.target.value)}
              />
            </div>
          ))}
        </div>
        
        <Callout type="tip" title="Examples of 12-Month Goals">
          <ul className="text-sm space-y-1">
            <li>• "Have $2,000 emergency fund"</li>
            <li>• "Pay off all credit card debt"</li>
            <li>• "Increase income by taking on side hustle"</li>
            <li>• "Start saving for [specific goal]"</li>
          </ul>
        </Callout>
      </div>
      
      {/* 3-5 Year Vision */}
      <SectionTitle>
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-primary" />
          Step 5: Your 3-5 Year Vision
        </div>
      </SectionTitle>
      <Paragraph>
        Dream a little. Where do you want to be in 3-5 years? What does financial success 
        look like for you?
      </Paragraph>
      
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 my-6">
        <WorksheetInput
          label="In 3-5 years, I see myself..."
          placeholder="Describe your vision: Where are you living? What are you doing? How does money feel?"
          value={data.goals3to5Years || ''}
          onChange={(v) => updateData('goals3to5Years', v)}
          multiline
        />
      </div>
      
      {/* Action Items */}
      <SectionTitle>Step 6: Immediate Action Items</SectionTitle>
      <Paragraph>
        What will you do THIS WEEK to start on your 90-day goals?
      </Paragraph>
      
      <div className="space-y-3 my-6">
        <CheckboxItem checked={data.action1Done || false} onChange={(v) => updateData('action1Done', v)}>
          <input
            type="text"
            className="flex-1 bg-transparent focus:outline-none"
            placeholder="Action item 1"
            value={data.action1 || ''}
            onChange={(e) => updateData('action1', e.target.value)}
          />
        </CheckboxItem>
        <CheckboxItem checked={data.action2Done || false} onChange={(v) => updateData('action2Done', v)}>
          <input
            type="text"
            className="flex-1 bg-transparent focus:outline-none"
            placeholder="Action item 2"
            value={data.action2 || ''}
            onChange={(e) => updateData('action2', e.target.value)}
          />
        </CheckboxItem>
        <CheckboxItem checked={data.action3Done || false} onChange={(v) => updateData('action3Done', v)}>
          <input
            type="text"
            className="flex-1 bg-transparent focus:outline-none"
            placeholder="Action item 3"
            value={data.action3 || ''}
            onChange={(e) => updateData('action3', e.target.value)}
          />
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Know your current season—meet yourself where you are",
        "Let your values guide your goals",
        "90-day goals create momentum",
        "12-month goals build transformation",
        "3-5 year vision keeps you inspired",
        "Immediate action starts the journey"
      ]} />
      
      <AffirmationBox>
        I have a clear vision for my financial future and a practical plan to get there. 
        One step at a time, I am building the life I want.
      </AffirmationBox>
    </WorkbookPage>
  );
};
